export interface ProductCategoryFlat {
  Slug: string;
  Title: string;
  position?: number | null;
  MenuLabel?: string | null;
  parent?: { Slug: string } | null;
  PreviewImage?: { url: string } | null;
}

export interface ProductTreeNode {
  slug: string;
  title: string;
  menuLabel: string;
  position: number;
  previewImageUrl?: string;
  parentSlug: string | null;
  children: ProductTreeNode[];
}

export interface ProductTreeProduct {
  slug: string;
  title: string;
  productName?: string;
  seriesName?: string;
  categorySlug: string;
  categoryPath: string[];
  headerImageUrl?: string;
  thumbnailUrl?: string;
}

const PRODUCTS_PREFIX = '/products';

export function buildParentMap(
  nodes: ProductCategoryFlat[],
): Map<string, string | null> {
  const map = new Map<string, string | null>();
  for (const node of nodes) {
    map.set(node.Slug, node.parent?.Slug ?? null);
  }
  return map;
}

export function getCategoryPath(
  slug: string,
  parentMap: Map<string, string | null>,
): string[] {
  const parts: string[] = [];
  let current: string | null = slug;
  const guard = new Set<string>();

  while (current) {
    if (guard.has(current)) break;
    guard.add(current);
    parts.unshift(current);
    current = parentMap.get(current) ?? null;
  }

  return parts;
}

export function categoryPathToUrl(pathSlugs: string[]): string {
  if (!pathSlugs.length) return PRODUCTS_PREFIX;
  return `${PRODUCTS_PREFIX}/${pathSlugs.join('/')}`;
}

export function productPathToUrl(
  categoryPath: string[],
  productSlug: string,
): string {
  return `${categoryPathToUrl(categoryPath)}/${productSlug}`;
}

export function buildProductTree(
  nodes: ProductCategoryFlat[],
): ProductTreeNode[] {
  const parentMap = buildParentMap(nodes);
  const nodeBySlug = new Map<string, ProductTreeNode>();

  for (const node of nodes) {
    nodeBySlug.set(node.Slug, {
      slug: node.Slug,
      title: node.Title,
      menuLabel: node.MenuLabel || node.Title,
      position: node.position ?? 0,
      previewImageUrl: node.PreviewImage?.url,
      parentSlug: parentMap.get(node.Slug) ?? null,
      children: [],
    });
  }

  const roots: ProductTreeNode[] = [];

  for (const treeNode of nodeBySlug.values()) {
    if (treeNode.parentSlug && nodeBySlug.has(treeNode.parentSlug)) {
      nodeBySlug.get(treeNode.parentSlug)?.children.push(treeNode);
    } else {
      roots.push(treeNode);
    }
  }

  const sortNodes = (list: ProductTreeNode[]) => {
    list.sort((a, b) => a.position - b.position);
    list.forEach((child) => sortNodes(child.children));
  };

  sortNodes(roots);
  return roots;
}

const MENU_ROOT_SLUG = 'machines';

/**
 * Limits direct children under `machines` to CMS-selected families (Machines Page).
 * Preserves MachineCategories order; deeper tree nodes are unchanged.
 */
export function applyMachinesMenuCategories(
  roots: ProductTreeNode[],
  orderedSlugs: string[],
): ProductTreeNode[] {
  if (!orderedSlugs.length) return roots;

  const cloneNode = (node: ProductTreeNode): ProductTreeNode => ({
    ...node,
    children: node.children.map(cloneNode),
  });

  return roots.map((root) => {
    if (root.slug !== MENU_ROOT_SLUG) return cloneNode(root);

    const childBySlug = new Map(root.children.map((child) => [child.slug, child]));
    const filteredChildren = orderedSlugs
      .map((slug) => childBySlug.get(slug))
      .filter((child): child is ProductTreeNode => Boolean(child))
      .map(cloneNode);

    return {
      ...cloneNode(root),
      children: filteredChildren,
    };
  });
}

/** Find the Machines root even when orphan categories also appear as roots. */
export function findMachinesRoot(
  roots: ProductTreeNode[],
): ProductTreeNode | undefined {
  return roots.find((root) => root.slug === MENU_ROOT_SLUG);
}

/**
 * `machines` is the site root for the mega menu; columns start at its children.
 * Always prefer `machines` when present — orphan root categories must not become column 1.
 */
export function getMenuPathPrefix(roots: ProductTreeNode[]): string[] {
  if (findMachinesRoot(roots)) {
    return [MENU_ROOT_SLUG];
  }
  return [];
}

/** Deepest path from `basePath`, always choosing the first child at each level. */
export function getExpandedDefaultPath(
  roots: ProductTreeNode[],
  basePath: string[],
): string[] {
  const path = [...basePath];

  while (true) {
    const node = getNodeByPath(roots, path);
    const firstChild = node?.children[0];
    if (!firstChild) break;
    path.push(firstChild.slug);
  }

  return path;
}

export function getDefaultActivePath(roots: ProductTreeNode[]): string[] {
  const prefix = getMenuPathPrefix(roots);
  if (!prefix.length && roots[0]) {
    return getExpandedDefaultPath(roots, [roots[0].slug]);
  }
  return getExpandedDefaultPath(roots, prefix);
}

export function getNodeByPath(
  roots: ProductTreeNode[],
  pathSlugs: string[],
): ProductTreeNode | undefined {
  let level = roots;
  let found: ProductTreeNode | undefined;

  for (const slug of pathSlugs) {
    found = level.find((n) => n.slug === slug);
    if (!found) return undefined;
    level = found.children;
  }

  return found;
}

export function getChildrenForPath(
  roots: ProductTreeNode[],
  pathSlugs: string[],
): ProductTreeNode[] {
  if (!pathSlugs.length) return roots;
  const node = getNodeByPath(roots, pathSlugs);
  return node?.children ?? [];
}

export function flattenCategoryTitles(
  pathSlugs: string[],
  nodes: ProductCategoryFlat[],
): { slug: string; title: string }[] {
  const bySlug = new Map(nodes.map((n) => [n.Slug, n.Title]));
  return pathSlugs
    .map((slug) => ({ slug, title: bySlug.get(slug) ?? slug }))
    .filter((x) => x.title);
}
